import { create } from 'zustand';
import * as yup from 'yup';
import { getLS, setLS } from '@/common/service/storage/LocalStorage/LocalStorage';

export function createFormStore<FormValues>(opts: {
    initialValues: FormValues;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema?: yup.ObjectSchema<any>;
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
    debounceMs?: number;
    localStorageName?: string;
}) {
    type Errors = Partial<Record<keyof FormValues, string>>;
    type Touched = Partial<Record<keyof FormValues, boolean>>;

    let timer: NodeJS.Timeout | null = null;

    // Hydrate from localStorage synchronously at store creation
    const hydrated: FormValues = (() => {
        if (!opts.localStorageName) return opts.initialValues;
        const stored = getLS(opts.localStorageName);
        if (!stored || typeof stored !== 'object' || Array.isArray(stored))
            return opts.initialValues;
        const merged = { ...opts.initialValues };
        for (const key of Object.keys(opts.initialValues as object) as (keyof FormValues)[]) {
            if (
                key in stored &&
                stored[key as string] !== undefined &&
                stored[key as string] !== ''
            ) {
                merged[key] = stored[key as string];
            }
        }
        return merged;
    })();

    // Compute initial isValid
    const initialIsValid = (() => {
        if (!opts.schema) return false;
        try {
            opts.schema.validateSync(hydrated, { abortEarly: true });
            return true;
        } catch {
            return false;
        }
    })();

    return create<{
        values: FormValues;
        errors: Errors;
        touched: Touched;
        isValid: boolean;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updateField: (field: keyof FormValues, value: any, debounce?: boolean) => void;
        touchField: (field: keyof FormValues) => void;
        validate: () => void;
        validateField: (field: keyof FormValues) => void;
        recomputeIsValid: () => void;
        updateLsData: () => void;
        clear: () => void;
        // eslint-disable-next-line sonarjs/cognitive-complexity
    }>((set, get) => ({
        values: hydrated,
        errors: {},
        touched: {},
        isValid: initialIsValid,

        updateField: (field, value, debounce) => {
            const { schema, validateOnChange, debounceMs = 0 } = opts;

            set((state) => ({
                values: { ...state.values, [field]: value },
            }));

            if (!schema || !validateOnChange) return;

            if (debounce && debounceMs > 0) {
                if (timer) clearTimeout(timer);
                timer = setTimeout(() => get().validate(), debounceMs);
            } else {
                get().validate();
            }
        },

        touchField: (field) => {
            set((state) => ({
                touched: { ...state.touched, [field]: true },
            }));

            if (opts.validateOnBlur && opts.schema) {
                get().validateField(field);
            }
        },

        validateField: (field) => {
            const { schema } = opts;
            if (!schema) return;

            try {
                schema.validateSyncAt(field as string, get().values);
                set((state) => ({
                    errors: { ...state.errors, [field]: undefined },
                }));
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                set((state) => ({
                    errors: { ...state.errors, [field]: err.message },
                }));
            }

            get().recomputeIsValid();
        },

        validate: () => {
            const { schema } = opts;
            if (!schema) return;

            try {
                schema.validateSync(get().values, { abortEarly: false });

                get().updateLsData();

                set({ errors: {}, isValid: true });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                const map: Errors = {};
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                err.inner.forEach((e: any) => {
                    map[e.path as keyof FormValues] = e.message;
                });
                set({ errors: map, isValid: false });
            }
        },

        recomputeIsValid: () => {
            const errors = get().errors;
            const isValid =
                Object.values(errors).every((e) => !e) &&
                (!opts.schema ||
                    (() => {
                        try {
                            opts.schema.validateSync(get().values, { abortEarly: true });
                            return true;
                        } catch {
                            return false;
                        }
                    })());

            set({ isValid });
        },

        updateLsData: () => {
            if (!opts.localStorageName) return;

            const curData = getLS(opts.localStorageName);

            if (!curData) {
                setLS(opts.localStorageName, get().values);
            }

            if (typeof curData === 'object' && !Array.isArray(curData)) {
                const newData = {
                    ...curData,
                    ...get().values,
                };

                setLS(opts.localStorageName, newData);
            }
        },

        clear: () => {
            set({
                values: opts.initialValues,
                errors: {},
                touched: {},
                isValid: false,
            });
        },
    }));
}
